import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import api from '../../api/api';
import { to } from '../../util/util';
import { history } from '../../index';
import { HolderDetailsResult } from '../../model/HolderDetailsResult';

interface IProps {
    ticker: string;
    exchange: string;
    combinedTicker: string;
    holderId?: string;
    holderNumber?: string;
    name?: string;
    holding?: string;
}

const Wrapper = styled.div`
    .holder-details__back {
        cursor: pointer;
        color: #0b5cad;
        margin: 0.5rem 0;
        display: inline-block;
    }
    .badge {
        color: #fff;
        font-size: 0.65em;
        padding: 2px 8px;
        border-radius: 3px;
        margin-left: 0.75rem;
        vertical-align: middle;
    }
    .badge--new { background: #3498db; }
    .badge--subscribed { background: #27ae60; }
    .badge--unsubscribed { background: #c0392b; }
    .badge--meta { background: #7f8c8d; margin: 0 0.5rem 0 0; }
    .badge--action { background: #3498db; margin-left: 0.5rem; }
    dd .badge { font-size: 0.8em; }
    .holder-details__welcome {
        margin-left: 0.75rem;
        vertical-align: middle;
    }
    .holder-details__cards {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;
        margin: 1rem 0;
    }
    .holder-details__card {
        background: #f6f9fc;
        border-radius: 4px;
        padding: 1rem;
        flex: 1;
        min-width: 260px;
    }
    .holder-details__card h4 {
        margin: 0 0 0.75rem;
    }
    .holder-details__card dl {
        margin: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.35rem 1rem;
    }
    .holder-details__card dt {
        color: #555;
        white-space: nowrap;
    }
    .holder-details__card dd {
        margin: 0;
    }
    .profit { color: #1a7f37; }
    .loss { color: #c0392b; }
    table {
        width: 100%;
        border-collapse: collapse;
        margin: 1rem 0 0; /* override the 3rem 0 from _base.css */
    }
    th, td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #e3e8ee;
        text-align: left;
    }
    th {
        background: #f6f9fc;
        color: #333;
        font-weight: 600;
    }
    td.num, th.num {
        text-align: right;
        white-space: nowrap;
    }
    .holder-details__activity {
        margin: 1rem 0;
    }
    .holder-details__activity table {
        background: #fff;
    }
`;

const fmt = (n: number | undefined, decimals: number = 0): string =>
    n === undefined || n === null ? '' : n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtDate = (d: string): string => d ? moment(d).format('DD MMM YYYY') : '';

const HolderDetails: React.FC<IProps> = (props: IProps) => {

    const [result, setResult] = useState<HolderDetailsResult | null>(null);
    const [error, setError] = useState('');
    const [showAllMovements, setShowAllMovements] = useState(false);

    useAsyncEffect(async (isMounted: () => boolean) => {
        const params: {holderId?:string, holderNumber?:string, name?:string, holding?:string} = {};
        if (props.holderId) params.holderId = props.holderId;
        else if (props.holderNumber) params.holderNumber = props.holderNumber;
        else if (props.name) { params.name = props.name; if (props.holding) params.holding = props.holding; }
        const [err, data] = await to(api().getHolderDetails(props.ticker, props.exchange, params));
        if (!isMounted()) return;
        if (err || !data.success) {
            setError(err?.message || data?.message || 'Failed to fetch holder details');
        } else {
            setResult(data);
            // canonicalize the URL to the stable holderId so it can be bookmarked/shared
            if (!props.holderId && data.holder?.holderId) {
                window.history.replaceState({}, document.title,
                    `${window.location.pathname}?holderId=${data.holder.holderId}`);
            }
        }
    }, []);

    const backToList = () => {
        const base = window.location.pathname.split('/').slice(0, 4).join('/');
        history.push(`${base}/Search`);
    };
    const back = <span className="holder-details__back" onClick={backToList}>&larr; Back to Register</span>;

    if (error) return <Wrapper>{back}<p style={{ color: '#c0392b' }}>{error}</p></Wrapper>;
    if (!result) return <Wrapper>{back}<p>Loading holder details from Automic&hellip; (first load can take a minute while reports generate)</p></Wrapper>;

    const holder = result.holder!;
    const summary = result.summary!;
    const movements = result.movements || [];
    // movements arrive oldest-first (the chart relies on that); the table shows most recent first
    const recentMovements = [...movements].reverse();
    const shownMovements = showAllMovements ? recentMovements : recentMovements.slice(0, 5);
    const activity = result.activity || [];

    // one step series of holdings per security (ordinary + options etc.), each extended
    // to today with its latest balance
    const securityCodes = Array.from(new Set(movements.map(m => m.securityCode || props.ticker)));
    if (securityCodes.length === 0) securityCodes.push(props.ticker);
    const multiSecurity = securityCodes.length > 1;
    const holdingSeries = securityCodes.map(code => {
        const ms = movements.filter(m => (m.securityCode || props.ticker) === code);
        const data: (string | number)[][] = ms.map(m => [m.date, m.closingBalance]);
        if (data.length > 0) {
            const latest = code === props.ticker ? summary.currentBalance : ms[ms.length - 1].closingBalance;
            data.push([moment().format('YYYY-MM-DD'), latest]);
        }
        return { name: `${code} Holding`, type: 'line', step: 'end', symbol: 'none', data, yAxisIndex: 0 };
    });
    // for new holders, only chart prices from 1 year before the first investment so recent activity is legible
    const firstInvested = holder.firstInvestmentDate || movements[0]?.date;
    const priceFrom = result.isNew && firstInvested ? moment(firstInvested).subtract(1, 'year') : null;
    const priceData = (result.priceSeries || [])
        .filter(p => !priceFrom || moment(p.date).isSameOrAfter(priceFrom))
        .map(p => [p.date, p.close]);

    const chartOption = {
        tooltip: { trigger: 'axis' },
        legend: { data: [...holdingSeries.map(s => s.name), 'Share Price'] },
        xAxis: { type: 'time' },
        yAxis: [
            { type: 'value', name: 'Holding', axisLabel: { formatter: (v: number) => v.toLocaleString() } },
            { type: 'value', name: 'Share Price', position: 'right', splitLine: { show: false } },
        ],
        series: [
            ...holdingSeries,
            { name: 'Share Price', type: 'line', symbol: 'none', data: priceData, yAxisIndex: 1 },
        ],
    };

    return <Wrapper>
        {back}
        <h2>{holder.name}{result.isNew && <span className="badge badge--new">NEW</span>}</h2>
        <div className="holder-details__cards">
            <div className="holder-details__card">
                <h4>Holder Details</h4>
                <dl>
                    <dt>Holder No.</dt><dd>{holder.holderNumber}</dd>
                    <dt>Holder Id</dt><dd>{holder.holderId}</dd>
                    <dt>Type</dt><dd>{holder.holderType}</dd>
                    <dt>Address</dt><dd>{holder.address}</dd>
                    <dt>Email</dt><dd>{holder.email}
                        {holder.email && (result.unsubscribed
                            ? <span className="badge badge--unsubscribed">Unsubscribed</span>
                            : result.subscribedOn
                                ? <span className="badge badge--subscribed">Subscribed on {moment(result.subscribedOn).format('DD-MMM-YYYY')}</span>
                                : <button className="button-sm holder-details__welcome" onClick={() =>
                                    history.push(`/SendWelcomeEmail/${props.combinedTicker}/${holder.holderId}?email=${encodeURIComponent(holder.email)}&name=${encodeURIComponent(holder.name)}`)
                                }>Send Welcome Email</button>)}
                    </dd>
                    <dt>Phone</dt><dd>{holder.phone}</dd>
                    <dt>First Invested</dt><dd>{fmtDate(holder.firstInvestmentDate)}</dd>
                </dl>
            </div>
            <div className="holder-details__card">
                <h4>Position</h4>
                <dl>
                    <dt>Current Holding</dt><dd>{fmt(summary.currentBalance)}</dd>
                    <dt>Current Price</dt><dd>${fmt(summary.currentPrice, 3)}</dd>
                    <dt>Current Value</dt><dd>${fmt(summary.currentValue, 2)}</dd>
                </dl>
            </div>
        </div>

        <ReactECharts option={chartOption} style={{ height: 400 }} notMerge={true} />

        <h3>Movement History</h3>
        <table>
            <thead>
                <tr>
                    <th>Date</th>
                    {multiSecurity && <th>Security</th>}
                    <th>Description</th>
                    <th>Broker</th>
                    <th className="num">Movement</th>
                    <th className="num">Price</th>
                    <th className="num">Closing Balance</th>
                </tr>
            </thead>
            <tbody>
                {shownMovements.map((m, i) =>
                    <tr key={i}>
                        <td>{fmtDate(m.date)}</td>
                        {multiSecurity && <td>{m.securityCode || props.ticker}</td>}
                        <td>{m.description}</td>
                        <td>{m.broker}</td>
                        <td className={"num " + (m.movement >= 0 ? 'profit' : 'loss')}>{fmt(m.movement)}</td>
                        <td className="num">{m.price != null ? `$${fmt(m.price, 3)}${m.priceEstimated ? '*' : ''}` : (m.priceMissing ? '†' : '')}</td>
                        <td className="num">{fmt(m.closingBalance)}</td>
                    </tr>
                )}
                {movements.length === 0 && <tr><td colSpan={multiSecurity ? 7 : 6}>No movements found</td></tr>}
            </tbody>
        </table>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'0.35rem'}}>
            <p style={{fontSize:'0.8rem', fontStyle:'italic', margin:0}}>* the registry does not provide a transaction price for this movement — estimated from the closing price on the trade date</p>
            {movements.length > 5 && <span style={{fontSize:'0.85rem', color:'#555', whiteSpace:'nowrap'}}>
                showing {shownMovements.length} of {movements.length}&nbsp;&nbsp;
                <button className="button-sm" onClick={() => setShowAllMovements(!showAllMovements)}>
                    {showAllMovements ? 'Show Fewer' : 'Show All'}
                </button>
            </span>}
        </div>
        {movements.some(m => m.priceMissing) && <p style={{fontSize:'0.8rem', fontStyle:'italic'}}>† no closing price found near the trade date</p>}

        <div className="holder-details__card holder-details__activity">
            <h4>Website and Email Activity</h4>
            {activity.length === 0 && <p>No activity found{holder.email ? ` for ${holder.email}` : ''}</p>}
            {activity.length > 0 && <table>
                <thead>
                    <tr><th>Date</th><th>Type</th><th>Description</th></tr>
                </thead>
                <tbody>
                    {activity.map((a, i) =>
                        <tr key={i}>
                            <td style={{whiteSpace:'nowrap'}}>{moment(a.date).format('DD MMM YYYY HH:mm')}</td>
                            <td style={{whiteSpace:'nowrap'}}>{a.type}</td>
                            <td>
                                {a.meta && <span className="badge badge--meta">{a.meta}</span>}
                                {a.description}
                                {a.actions?.map(x => <span key={x} className="badge badge--action">{x}</span>)}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>}
        </div>
    </Wrapper>
}

export default HolderDetails;
